import { useLocalization } from '~/framework';
import {
   Button,
   Popover,
   PopoverBody,
   PopoverContent,
   PopoverTrigger,
   useDisclosure,
} from '@chakra-ui/react';
import { useRef, useEffect, useState } from 'react';
import { useTeam, useTeams } from '~/pages/_shared';
import { Icon } from '~/ui-components';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { TeamSearchInput, RecentTeams } from '~/pages/_shared/TeamSearchInput';
import { truncate } from 'lodash';

const MAX_RECENT_TEAMS = 3;
const MAX_TEAM_DISPLAY_LENGTH = 30;

type SignoffTeamSelectorProps = {
   teamid?: number;
   onSelect: (teamid: number, name: string, avatarUrl?: string) => void;
};

export const SignoffTeamSelector = ({ teamid, onSelect }: SignoffTeamSelectorProps) => {
   const { _js } = useLocalization();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const initialFocusRef = useRef(null);
   const { data: assignedTeam, isLoading } = useTeam(teamid ?? 0);
   const recentTeamids = useRef<number[]>([]);
   const { data: recentTeams, isLoading: isLoadingRecentTeams } = useTeams(recentTeamids.current);
   const [teamName, setTeamName] = useState<string>('');

   useEffect(() => {
      recentTeamids.current =
         localStorage
            .getItem('teamids')
            ?.split(',')
            .map(teamid => parseInt(teamid)) ?? [];
   }, [recentTeamids]);

   let recentTeamsProp: RecentTeams = [];
   if (!isLoadingRecentTeams && recentTeams) {
      recentTeamsProp = recentTeams.map(team => {
         return {
            teamid: team.teamid,
            name: team.name,
            image: team.image,
            avatarUrl: team.avatarUrl,
         };
      });
   }

   useEffect(() => {
      if (assignedTeam !== undefined && !isLoading) {
         let newTeamName = assignedTeam?.name ?? _js('Pick a signoff team');
         setTeamName(truncate(newTeamName, { length: MAX_TEAM_DISPLAY_LENGTH }));
      }
   }, [assignedTeam, isLoading, _js]);

   const assignTeam = (teamid: number, name: string, avatarUrl?: string) => {
      onClose();
      updateRecentTeams(teamid);
      onSelect(teamid, name, avatarUrl);
   };

   const updateRecentTeams = (teamid: number) => {
      let newRecentTeams =
         localStorage
            .getItem('teamids')
            ?.split(',')
            .map(teamid => parseInt(teamid)) ?? [];

      let index = newRecentTeams.findIndex(value => value === teamid);
      if (index !== -1) {
         newRecentTeams.splice(index, 1);
      }

      newRecentTeams.unshift(teamid);

      while (newRecentTeams.length > MAX_RECENT_TEAMS) {
         newRecentTeams.pop();
      }

      localStorage.setItem('teamids', newRecentTeams.toString());
      recentTeamids.current = newRecentTeams;
   };

   return (
      <Popover
         closeOnBlur={true}
         closeOnEsc={true}
         returnFocusOnClose={true}
         isOpen={isOpen}
         onClose={onClose}
         autoFocus={true}
         initialFocusRef={initialFocusRef}
      >
         <PopoverTrigger>
            <Button
               onClick={onOpen}
               w={250}
               rightIcon={<Icon type={faChevronDown} fontSize="11px" />}
               colorScheme="clear"
               variant="outline"
               justifyContent="space-between"
               aria-label={_js('Select Signoff Team')}
            >
               {teamName}
            </Button>
         </PopoverTrigger>
         <PopoverContent>
            <PopoverBody>
               <TeamSearchInput
                  refFocus={initialFocusRef}
                  onChange={assignTeam}
                  recentTeams={recentTeamsProp}
               />
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
};
